import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Check, Copy, Layers, Loader2, RotateCcw, Trash2 } from "lucide-react";
import { apiClient } from "@/lib/auth";
import { TraceCard } from "./Trace";

export const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Copied — paste it to the agent")).catch(() => toast.error("Couldn't copy"));
const err = (e) => e?.response?.data?.detail || "Something went wrong";
const day = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

export function PromptBox({ text, testid }) {
  return (
    <div data-testid={testid} className="relative rounded-md border border-brand-cyan/30 bg-navy-950/70 p-3">
      <pre className="whitespace-pre-wrap text-[12px] leading-relaxed text-slate-200 font-mono max-h-64 overflow-y-auto pr-8">{text}</pre>
      <button type="button" onClick={() => copy(text)} data-testid={`${testid}-copy`} className="absolute top-2 right-2 p-1.5 rounded border border-white/10 text-slate-300 hover:text-brand-cyan" title="Copy"><Copy className="h-3.5 w-3.5" /></button>
    </div>
  );
}

function Item({ x, first, last, onMove, onChanged }) {
  const [open, setOpen] = useState(false);
  const act = async (fn) => { try { await fn(); onChanged(); } catch (e) { toast.error(err(e)); } };
  return (
    <li data-testid={`ally-item-${x.id}`} className={`rounded-md border border-white/10 bg-white/[0.02] p-3 space-y-2 ${x.done ? "opacity-55" : ""}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm text-slate-100 leading-snug">{x.problem}</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-slate-500 mt-0.5">{day(x.created_at)} · {x.screen?.title}{x.option?.title ? ` · ${x.option.title}` : ""}
            {x.scope === "platform" && <span data-testid={`ally-item-platform-${x.id}`} className="ml-1.5 text-amber-300">· Platform</span>}</div>
        </div>
        {!x.done && <div className="flex flex-col">
          <button type="button" disabled={first} onClick={() => onMove(-1)} data-testid={`ally-item-up-${x.id}`} className="p-0.5 text-slate-500 hover:text-slate-200 disabled:opacity-20"><ArrowUp className="h-3.5 w-3.5" /></button>
          <button type="button" disabled={last} onClick={() => onMove(1)} data-testid={`ally-item-down-${x.id}`} className="p-0.5 text-slate-500 hover:text-slate-200 disabled:opacity-20"><ArrowDown className="h-3.5 w-3.5" /></button>
        </div>}
      </div>
      <div className="flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-[0.14em]">
        <button type="button" onClick={() => setOpen(!open)} data-testid={`ally-item-prompt-${x.id}`} className="text-brand-cyan hover:underline">{open ? "Hide prompt" : "Agent prompt"}</button>
        <button type="button" onClick={() => copy(x.prompt)} data-testid={`ally-item-copy-${x.id}`} className="inline-flex items-center gap-1 text-slate-300 hover:text-brand-cyan"><Copy className="h-3 w-3" /> Copy</button>
        <button type="button" onClick={() => act(() => apiClient.patch(`/ally/build-list/${x.id}`, { done: !x.done }))} data-testid={`ally-item-done-${x.id}`} className="inline-flex items-center gap-1 text-emerald-300 hover:underline">{x.done ? <><RotateCcw className="h-3 w-3" /> Reopen</> : <><Check className="h-3 w-3" /> Mark done</>}</button>
        <button type="button" onClick={() => window.confirm("Delete this item?") && act(() => apiClient.delete(`/ally/build-list/${x.id}`))} data-testid={`ally-item-delete-${x.id}`} className="inline-flex items-center gap-1 text-red-300 hover:underline"><Trash2 className="h-3 w-3" /> Delete</button>
      </div>
      {open && x.trace && <TraceCard trace={x.trace} testid={`ally-item-trace-${x.id}`} />}
      {open && <PromptBox text={x.prompt} testid={`ally-item-promptbox-${x.id}`} />}
    </li>
  );
}

/** Shared Build List: grouped by engine, reorder, done, delete, and combine every open item into one prompt. */
export default function BuildList({ tick }) {
  const [items, setItems] = useState(null);
  const [combined, setCombined] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => apiClient.get("/ally/build-list").then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, [tick]);
  if (!items) return <Loader2 className="h-5 w-5 animate-spin text-brand-cyan" />;
  const open = items.filter((x) => !x.done);
  const move = async (id, dir) => {
    const ids = open.map((x) => x.id); const i = ids.indexOf(id); const j = i + dir;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setItems([...ids.map((k) => items.find((x) => x.id === k)), ...items.filter((x) => x.done)]);
    await apiClient.put("/ally/build-list/order", { ids }).catch(() => toast.error("Couldn't save the order"));
  };
  const combine = async () => {
    setBusy(true);
    try { const { data } = await apiClient.post("/ally/build-list/combine"); setCombined(data); } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const groups = [...new Set(items.map((x) => x.engine || "Platform"))];
  return (
    <div data-testid="ally-build-list" className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-slate-300">{open.length} open · {items.length - open.length} done</span>
        <button type="button" disabled={!open.length || busy} onClick={combine} data-testid="ally-combine"
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-brand-cyan/50 text-brand-cyan font-mono text-[10px] uppercase tracking-[0.14em] hover:bg-brand-cyan/10 disabled:opacity-40">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Layers className="h-3.5 w-3.5" />} Combine into one session</button>
      </div>
      {combined && <div className="space-y-1" data-testid="ally-combined"><div className="text-xs text-slate-400">One prompt for {combined.count} open items — paste it to the agent in one go.</div><PromptBox text={combined.prompt} testid="ally-combined-prompt" /></div>}
      {!items.length && <p data-testid="ally-build-empty" className="text-sm text-slate-400">Nothing saved yet. Use Fix or improve on any screen, then tap "Save to my build list".</p>}
      {groups.map((g) => {
        const rows = items.filter((x) => (x.engine || "Platform") === g);
        return (
          <section key={g} data-testid={`ally-group-${g.replace(/\s+/g, "-").toLowerCase()}`} className="space-y-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{g} · {rows.length}</div>
            <ul className="space-y-2">{rows.map((x) => { const i = open.findIndex((o) => o.id === x.id);
              return <Item key={x.id} x={x} first={i === 0} last={i === open.length - 1} onMove={(d) => move(x.id, d)} onChanged={load} />; })}</ul>
          </section>);
      })}
    </div>
  );
}
